import { Logger } from '@shared/lib/logger';
import { wipeOrigin } from './wipe';

const logger = new Logger('app-key-guard');

/** Where the running app's key is remembered between visits. */
export const APP_KEY_STORAGE_KEY = 'app_key';

// Set once a reset has run for this key, so a browser that can't persist the
// new key (blocked storage) doesn't reset and reload forever.
const RESET_DONE_KEY = 'app_key_reset';
// The same marker in `window.name`, which survives a reload without relying
// on storage at all: whatever storage does, one tab resets at most once.
const RESET_DONE_WINDOW_NAME = 'cf-app-key-reset:';

function alreadyReset(appKey: string): boolean {
	if (window.name === RESET_DONE_WINDOW_NAME + appKey) {
		return true;
	}
	try {
		return sessionStorage.getItem(RESET_DONE_KEY) === appKey;
	} catch {
		return true;
	}
}

/**
 * True when this origin last ran a *different* app (its stored app key isn't
 * this build's). A different key is a different publisher's app -- on
 * localhost, or a domain re-pointed at another tenant -- so nothing the
 * previous one stored (session, downloads, cached content) belongs here.
 *
 * Not the same as a release: a new `version` of the same app keeps the
 * reader's session and settings (see `preserve-on-deploy.ts`).
 */
export function hasAppChanged(appKey: string): boolean {
	try {
		const stored = localStorage.getItem(APP_KEY_STORAGE_KEY);
		if (!stored || stored === appKey) {
			return false;
		}
		return !alreadyReset(appKey);
	} catch {
		return false;
	}
}

/**
 * Wipes everything the previous app stored on this origin -- localStorage,
 * sessionStorage, every Cache Storage cache and every IndexedDB database --
 * and records `appKey` as the app now running. The caller reloads afterwards,
 * so every module starts from the empty state rather than from what it read
 * at import time.
 */
export async function resetForNewApp(appKey: string): Promise<void> {
	logger.warn(
		`Stored app key differs from this app's (${appKey}): clearing all stored data and reloading once`,
	);
	window.name = RESET_DONE_WINDOW_NAME + appKey;
	await wipeOrigin();
	try {
		localStorage.setItem(APP_KEY_STORAGE_KEY, appKey);
		sessionStorage.setItem(RESET_DONE_KEY, appKey);
	} catch {
		// Storage unavailable: nothing more to record.
	}
}
